/**
 * The watch's slice as a view state: loading, absent (the module is not running on this hub),
 * error, or ready. The slice normally arrives with the first state and every SSE 'ext' update;
 * when the first state came without it, the console asks GET /api/watch once to tell an older
 * hub (404) from a failure.
 */
import { useEffect } from 'react';
import type { WatchState } from '@orgus/protocol';
import { ApiError } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';
import { create } from 'zustand';
import { loadWatch } from './api.ts';
import { watchStateOf } from './model.ts';

export type WatchView =
  | { kind: 'loading' }
  | { kind: 'absent' }
  | { kind: 'error'; error: unknown; retry: () => Promise<void> }
  | { kind: 'ready'; state: WatchState };

interface Probe {
  status: 'idle' | 'loading' | 'absent' | 'error';
  error: unknown;
}

const useProbe = create<Probe>()(() => ({ status: 'idle', error: null }));

async function probe(): Promise<void> {
  if (useProbe.getState().status === 'loading') return;
  useProbe.setState({ status: 'loading', error: null });
  try {
    await loadWatch();
    useProbe.setState({ status: 'idle', error: null });
  } catch (e) {
    useProbe.setState(e instanceof ApiError && e.status === 404 ? { status: 'absent', error: null } : { status: 'error', error: e });
  }
}

/** Forget the last probe (tests). */
export function resetWatchProbe(): void {
  useProbe.setState({ status: 'idle', error: null });
}

export function useWatch(): WatchView {
  const raw = useOrgus((s) => s.ext.watch);
  const ready = useOrgus((s) => s.phase === 'ready');
  const live = useOrgus((s) => s.mode !== 'mock');
  const p = useProbe();
  const state = watchStateOf({ watch: raw });
  const missing = ready && live && !state;
  useEffect(() => {
    if (missing && useProbe.getState().status === 'idle') void probe();
  }, [missing]);
  if (state) return { kind: 'ready', state };
  if (!ready || p.status === 'loading' || (missing && p.status === 'idle')) return { kind: 'loading' };
  if (p.status === 'error') return { kind: 'error', error: p.error, retry: probe };
  return { kind: 'absent' };
}
